import React from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { FiHeart, FiShoppingCart } from 'react-icons/fi';
import { useShop } from '../../context/ShopContext';
import { useDiscount } from '../../context/DiscountContext';
import { cashPriceFromMrp, getProductDiscount, formatPrice } from '../../utils/cashPricing';
import { getStartingProductInstallment } from '../../utils/installmentCalculator';
import './ProductCard.css';

const ProductCard = ({ product }) => {
    const { toggleWishlist, isInWishlist, addToCart } = useShop();
    const { effectiveDiscount } = useDiscount();
    const inWishlist = isInWishlist(product._id);

    const handleWishlistClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        toggleWishlist(product);
        toast.success(
            inWishlist ? 'Removed from wishlist' : 'Saved to wishlist'
        );
    };

    const handleAddToCartClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        addToCart(product, 1);
    };

    const mrp = Number(product.mrp);
    const hasMrp = Number.isFinite(mrp) && mrp > 0;
    const discount = getProductDiscount(product, effectiveDiscount);
    const cashPrice = hasMrp ? cashPriceFromMrp(mrp, discount) : null;
    const hasCashDiscount = hasMrp && discount > 0 && cashPrice !== null && cashPrice < mrp;

    // Resolve starting monthly installment strictly from admin-configured product plans
    const startingInstallment = hasMrp ? getStartingProductInstallment(product) : null;

    const primaryImage = product.image || 'https://res.cloudinary.com/fl3tts9v/image/upload/f_auto,q_auto,w_600/v1790983151/category-air-conditioners_xtx2ok.jpg';
    const secondaryImage =
        product.secondary_image ||
        (Array.isArray(product.images) && product.images.length > 1 ? product.images[1] : null) ||
        (Array.isArray(product.gallery) && product.gallery.length > 0 ? product.gallery[0] : null) ||
        null;

    return (
        <div className={`product-card-showroom${secondaryImage ? ' has-secondary-image' : ''}`}>
            {/* Card Media Area */}
            <div className="product-card-showroom__media">
                <Link to={`/products/${product._id}`} aria-label={product.name} className="product-card-showroom__img-link">
                    <img
                        src={primaryImage}
                        alt={product.name}
                        loading="lazy"
                        className="product-card-showroom__img product-card-showroom__img--primary"
                        onError={(e) => {
                            e.currentTarget.src = 'https://res.cloudinary.com/fl3tts9v/image/upload/f_auto,q_auto,w_600/v1790983151/category-air-conditioners_xtx2ok.jpg';
                        }}
                    />
                    {secondaryImage && (
                        <img
                            src={secondaryImage}
                            alt={`${product.name} alternate angle`}
                            loading="lazy"
                            className="product-card-showroom__img product-card-showroom__img--secondary"
                            onError={(e) => {
                                e.currentTarget.style.display = 'none';
                            }}
                        />
                    )}
                </Link>

                {/* Single Clean Badge */}
                {hasCashDiscount && (
                    <span className="product-card-showroom__cash-badge" title={`${discount}% Instant Cash Discount`}>
                        <span className="product-card-showroom__cash-badge-full">{discount}% Instant Discount</span>
                        <span className="product-card-showroom__cash-badge-compact">{discount}% Off</span>
                    </span>
                )}

                {/* Floating Wishlist Heart */}
                <button
                    type="button"
                    className={`product-card-showroom__wishlist ${inWishlist ? 'is-active' : ''}`}
                    title={inWishlist ? 'Remove from wishlist' : 'Save to wishlist'}
                    onClick={handleWishlistClick}
                    aria-pressed={inWishlist}
                >
                    <FiHeart style={inWishlist ? { fill: '#EF4444', stroke: '#EF4444' } : undefined} />
                </button>
            </div>

            {/* Card Information */}
            <div className="product-card-showroom__info">
                {/* Brand & Stock */}
                <div className="product-card-showroom__meta">
                    <span className="product-card-showroom__brand">
                        {product.brand || 'RAHBER'}
                    </span>
                    <span className="product-card-showroom__stock-hint">
                        In Stock
                    </span>
                </div>

                {/* Title */}
                <Link to={`/products/${product._id}`} className="product-card-showroom__title-link">
                    <h3 className="product-card-showroom__title">{product.name}</h3>
                </Link>

                {/* Clean, Decent Price Block */}
                <div className="product-card-showroom__pricing">
                    <div className="product-card-showroom__price-row">
                        <span className="product-card-showroom__main-price">
                            {formatPrice(cashPrice || mrp)}
                        </span>
                        {hasCashDiscount && (
                            <span className="product-card-showroom__mrp-strike">
                                {formatPrice(mrp)}
                            </span>
                        )}
                    </div>

                    {startingInstallment && (
                        <span className="product-card-showroom__installment-note">
                            Installment from <strong>{formatPrice(startingInstallment.monthlyInstallment)}/mo</strong>
                        </span>
                    )}
                </div>

                {/* Add to Cart Button (Almumtaz style: text transforms to cart icon on hover) */}
                <button
                    type="button"
                    className="product-card-showroom__add-cart-btn"
                    onClick={handleAddToCartClick}
                    title={`Add ${product.name} to Cart`}
                    aria-label={`Add ${product.name} to Cart`}
                >
                    <span className="btn-text-content">
                        <FiShoppingCart className="product-card-showroom__btn-icon" />
                        <span>Add to Cart</span>
                    </span>
                    <span className="btn-icon-hover" aria-hidden="true">
                        <FiShoppingCart />
                    </span>
                </button>
            </div>
        </div>
    );
};

export default ProductCard;
